// src/pages/markets/board/api/useAbortableGet.ts
//
// The abort pattern used for the trader profile, generalised. Two guards, both
// needed: the AbortController stops the request on the wire when the key
// changes or the component unmounts, and the callId ref stops a response that
// was already in flight from overwriting newer state — an aborted fetch does
// not always lose the race to reject.
//
// Every hover-triggered request in the board goes through this (or through
// react-query's own `signal`, which does the same thing); a hover that leaves
// must not keep a socket open, because the reader hovers dozens of rows a
// minute and each one would otherwise queue behind the last.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../../api/apiClient';

export interface AbortableState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    reload: () => void;
}

export function useAbortableGet<T>(
    path: string | null,
    enabled = true,
): AbortableState<T> {
    const callIdRef = useRef(0);
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const reload = useCallback(() => setNonce(n => n + 1), []);

    useEffect(() => {
        if (!path || !enabled) {
            // Invalidate whatever is in flight; a disabled hook shows nothing.
            callIdRef.current++;
            setLoading(false);
            setData(null);
            setError(null);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setData(null);

        apiGet<T>(path, controller.signal)
            .then(res => {
                if (callId !== callIdRef.current) return;
                setData(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current) return;
                console.error(`${path} failed:`, err);
                setError(err instanceof Error ? err.message : String(err));
                setData(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => {
            // Bump first: the abort rejection must land on a stale callId.
            callIdRef.current++;
            controller.abort();
        };
    }, [path, enabled, nonce]);

    return { data, loading, error, reload };
}